import { UsedInOfflineMode } from '../../../../../../../offline/types/UsedInOfflineMode';

export interface OrderGeneralDataPermissionsParams extends UsedInOfflineMode {
  canEditOrder: boolean;
  isAdminBrand: boolean;
  isAdminTeceo: boolean;
  isSeller: boolean;
  isStore: boolean;
  enablePriceTableByRepresentative: boolean;
  blockOrderWithoutCustomer: boolean;
  orderSellerId?: string;
  currentUserId?: string;
  customerId?: string;
}

export interface OrderGeneralDataPermissions {
  canEditCustomer: boolean;
  canClearCustomer: boolean;
  canEditSeller: boolean;
  canEditSalesRepresentative: boolean;
  canEditPriceTable: boolean;
  canEditConsultant: boolean;
  isPriceTableVisible: boolean;
  shouldUseBrandPriceTables: boolean;
  shouldFetchSellers: boolean;
}

const canChangeSeller = ({
  isSeller,
  isStore,
  orderSellerId,
  currentUserId,
}: OrderGeneralDataPermissionsParams) => {
  if (isStore) {
    return false;
  }

  if (!isSeller) {
    return true;
  }

  return !orderSellerId || orderSellerId === currentUserId;
};

const canClearCustomer = ({
  isSeller,
  isStore,
  blockOrderWithoutCustomer,
}: OrderGeneralDataPermissionsParams) => {
  if (isStore) {
    return false;
  }

  return !isSeller || !blockOrderWithoutCustomer;
};

const canChangePriceTable = ({
  isAdminBrand,
  isAdminTeceo,
  isSeller,
  enablePriceTableByRepresentative,
  isOfflineMode,
}: OrderGeneralDataPermissionsParams) => {
  if (isOfflineMode) {
    return isAdminBrand;
  }

  return isAdminBrand || isAdminTeceo || (isSeller && enablePriceTableByRepresentative);
};

const canChangeSalesRepresentative = ({
  isSeller,
  isStore,
  customerId,
}: OrderGeneralDataPermissionsParams) => {
  if (isStore) {
    return false;
  }

  return !isSeller || Boolean(customerId);
};

export const buildOrderGeneralDataPermissions = (
  params: OrderGeneralDataPermissionsParams,
): OrderGeneralDataPermissions => {
  const { canEditOrder, isAdminBrand, isStore, isOfflineMode } = params;

  return {
    canEditCustomer: canEditOrder && !isStore,
    canClearCustomer: canEditOrder && canClearCustomer(params),
    canEditSeller: canEditOrder && canChangeSeller(params),
    canEditSalesRepresentative: canEditOrder && canChangeSalesRepresentative(params),
    canEditPriceTable: canEditOrder && canChangePriceTable(params),
    canEditConsultant: canEditOrder && !isOfflineMode,
    isPriceTableVisible: !isStore,
    shouldUseBrandPriceTables: isAdminBrand,
    shouldFetchSellers: isAdminBrand,
  };
};
